import { createContext, useContext, useRef } from "react";

import { useStore } from "zustand";

import {
  type IpdbState,
  type IpdbStore,
  type IpdbStoreApi,
  createIpdbStore
} from "./ipdbStore";

export const IpdbStoreContext = createContext<Maybe<IpdbStoreApi>>(null);

interface IpdbStoreProviderProps {
  readonly children: React.ReactNode;
  readonly overrides?: Partial<IpdbState>;
}

function IpdbStoreProvider({ children, overrides }: IpdbStoreProviderProps) {
  const storeRef = useRef<Maybe<IpdbStoreApi>>(null);
  if (storeRef.current === null) {
    storeRef.current = createIpdbStore(overrides);
  }
  return (
    <IpdbStoreContext.Provider value={storeRef.current}>
      {children}
    </IpdbStoreContext.Provider>
  );
}

/**
 * Hook to select state from the nearest IpdbStore.
 *
 * Usage (in a Container or Logic Hook — never in a Presenter):
 *   const showIpdb = useIpdbStore(selectShowIpdb);
 *
 * For compound selectors that return new object literals, wrap the selector
 * with `useShallow` at the call site to avoid re-renders on every store update:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const state = useIpdbStore(useShallow(selectIpdbState));
 */
function useIpdbStore<T>(selector: (state: IpdbStore) => T): T {
  const store = useContext(IpdbStoreContext);
  if (!store) {
    throw new Error("useIpdbStore must be used within a <IpdbStoreProvider>");
  }
  return useStore(store, selector);
}

export { IpdbStoreProvider, useIpdbStore };
